import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useRouteMatch } from 'react-router-dom';
import classNames from 'classnames';
import Collapse from '@material-ui/core/Collapse';

import { FormattedMessageWrappedInSpan } from '../../misc';
import SubsectionInFrame from '../../misc/SubsectionInFrame';
import { CATEGORY, DUAL, GPS, SMART_SEARCH, STRAVA } from '../../../utils/variables';
import { defineActiveSearchType } from '../../../utils/common';

const AdditionalSearchOptions = ({
  hasSmartSearchTab,
  hasCategorySearchTab,
  hasGpsSearchTab,
  hasDualSearchTab,
  setSearchType,
  hasEventPresale,
  onScrollToSearchOptions,
  triggerPointingAnimation,
  hasEventInactiveSearch,
  hasEventFaceSearch,
  hasEventCombinedSearch,
  selectedSearchType,
  isStravaSearchAvailable,
  setShowAfterSearchDialogState,
}) => {
  const match = useRouteMatch();
  const ALT_HINT = 'alternative_option_hint';
  const isMainEventPage = match.path === '/:language/event/:id';

  const [clickedOption, setClickedOption] = useState(null);
  const [animate, setAnimate] = useState(false);
  const [expand, setExpand] = useState(!isMainEventPage || hasEventInactiveSearch);

  const activeButton = defineActiveSearchType(match.url, hasEventPresale);

  useEffect(() => {
    if (triggerPointingAnimation) {
      setAnimate(true)
      const timeout = setTimeout(() => setAnimate(false), 2000);
      return () => clearTimeout(timeout);
    }
  }, [triggerPointingAnimation])

  useEffect(() => {
    if (selectedSearchType) {
      setClickedOption(selectedSearchType);
    }
  }, [selectedSearchType]);

  const goToNewSearchType = (searchType) => {
    if (setShowAfterSearchDialogState) {
      setShowAfterSearchDialogState(false);
    }
    if (activeButton === searchType && searchType === SMART_SEARCH) {
      setSearchType(ALT_HINT);
    } else {
      setSearchType(searchType);
    }
    setClickedOption(searchType);
    onScrollToSearchOptions && onScrollToSearchOptions();
  };

  return (
    <div
      className={classNames('subsection-container', {
        'point-to-frame': animate,
      })}
    >
      <SubsectionInFrame
        className="search-options mb-20"
        title={
          <FormattedMessageWrappedInSpan
            id="seachOptions.title.otherOptions"
            defaultMessage="Other search options"
            className="typography uppercase typography-title_small"
          />
        }
        color="medium"
        layout="column"
        gap={20}
        topPadding
      >
        {(isMainEventPage && !hasEventInactiveSearch) ? (
          <FormattedMessageWrappedInSpan
            id="seachOptions.description.useMainFirst"
            defaultMessage="There are advanced ways to find your photos. However, we recommend trying the main search first."
          />
        ) : (
          <FormattedMessageWrappedInSpan
            id="seachOptions.description.whatForAreOptions"
            defaultMessage="Find more photos of you"
          />
        )}
        {(isMainEventPage && !hasEventInactiveSearch) && (
          <button
            className={classNames(
              'btn btn-default secondary-new secondary-new-in-subsection',
              {
                'clicked-btn': expand,
              }
            )}
            onClick={() =>{ 
              setExpand((prevState) => !prevState)}}
          >
            {expand ? (
              <FormattedMessageWrappedInSpan
                id="seachOptions.showOptionsBtn.hide"
                defaultMessage="Hide search options"
              />
            ) : (
              <FormattedMessageWrappedInSpan
                id="seachOptions.showOptionsBtn.expand"
                defaultMessage="See more search options"
              />
            )}
          </button>
        )}
        <Collapse in={expand} timeout="auto" className="search-options-btns-wrapper">
          {hasSmartSearchTab && (
            <button
              className={classNames(
                'btn btn-default secondary-new secondary-new-in-subsection full-width',
                {
                  'active-option-btn': activeButton === SMART_SEARCH,
                  'clicked-btn': clickedOption == SMART_SEARCH,
                }
              )}
              onClick={() => goToNewSearchType(SMART_SEARCH)}
            >
              <FormattedMessageWrappedInSpan
                id="seachOptions.options.smart"
                defaultMessage="Smart"
              />
            </button>
          )}
          {hasCategorySearchTab && (
            <button
              className={classNames(
                'btn btn-default secondary-new secondary-new-in-subsection full-width',
                {
                  'active-option-btn': activeButton === CATEGORY,
                  'clicked-btn': clickedOption == CATEGORY,
                }
              )}
              onClick={() => goToNewSearchType(CATEGORY)}
            >
              <FormattedMessageWrappedInSpan
                id="seachOptions.options.category"
                defaultMessage="By category"
              />
            </button>
          )}
          {isStravaSearchAvailable && (
            <button
              className={classNames(
                'btn btn-default secondary-new secondary-new-in-subsection full-width',
                {
                  'active-option-btn': activeButton === STRAVA,
                  'clicked-btn': clickedOption == STRAVA,
                }
              )}
              onClick={() => goToNewSearchType(STRAVA)}
            >
              <FormattedMessageWrappedInSpan
                id="seachOptions.options.strava"
                defaultMessage="With Strava"
              />
            </button>
          )}
          {hasGpsSearchTab && (
            <button
              className={classNames(
                'btn btn-default secondary-new secondary-new-in-subsection full-width',
                {
                  'active-option-btn': activeButton === GPS,
                  'clicked-btn': clickedOption == GPS,
                }
              )}
              onClick={() => goToNewSearchType(GPS)}
            >
              <FormattedMessageWrappedInSpan
                id="seachOptions.options.gps"
                defaultMessage="With GPS file"
              />
            </button>
          )}
          {hasDualSearchTab && !hasEventCombinedSearch && (
            <button
              className={classNames(
                'btn btn-default secondary-new secondary-new-in-subsection full-width',
                {
                  'active-option-btn': activeButton === DUAL,
                  'clicked-btn': clickedOption == DUAL,
                }
              )}
              onClick={() => goToNewSearchType(DUAL)}
            >
              {hasEventFaceSearch ? (
                <FormattedMessageWrappedInSpan
                  id="seachOptions.options.dualByBib"
                  defaultMessage="Search by bib"
                />
              ) : (
                <FormattedMessageWrappedInSpan
                  id="seachOptions.options.dualByFace"
                  defaultMessage="Search with your selfie"
                />
              )}
            </button>
          )}
        </Collapse>
      </SubsectionInFrame>
    </div>
  );
};

AdditionalSearchOptions.propTypes = {
  hasSmartSearchTab: PropTypes.bool,
  hasCategorySearchTab: PropTypes.bool,
  hasGpsSearchTab: PropTypes.bool,
  hasDualSearchTab: PropTypes.bool,
  setSearchType: PropTypes.func,
  hasEventPresale: PropTypes.bool,
  onScrollToSearchOptions: PropTypes.func,
  triggerPointingAnimation: PropTypes.bool,
  hasEventInactiveSearch: PropTypes.bool,
  hasEventFaceSearch: PropTypes.bool,
  hasEventCombinedSearch: PropTypes.bool,
  selectedSearchType: PropTypes.string,
  isStravaSearchAvailable: PropTypes.bool,
  setShowAfterSearchDialogState: PropTypes.func,
};

export default AdditionalSearchOptions;
